import { FC, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router";
import { useImpersonation } from "../../../contexts/ImpersonationContext";
import Roles from "../../../models/roles";
import { AUTH_ROUTE } from "../../../pages/App/routes";
import { ConnectIDUserDetails as UserDetailsType } from "../../../services/ConnectIDService";
import {
  useAppDispatch as useDispatch,
  useAppSelector as useSelector,
} from "../../../store";
import { setShowNotVerifiedErrorAction } from "../../../store/auth/slice";
import { setCompleteLaterAction } from "../../../store/onboarding/slice";
import { sendConnectIdDetailsThunk } from "../../../store/onboarding/thunk";
import { TCompletionStatus } from "../../../store/onboarding/types";
import ConnectIDButton from "../../ConnectID/ConnectIDButton";
import CustomButton from "../../UI/Buttons/CustomButton";
import RightArrow from "../../UI/Icons/RightArrow";
import Warning from "../../UI/Icons/Warning";

interface IdVerificationProps {}

const IdVerfication: FC<IdVerificationProps> = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const {isImpersonating} = useImpersonation();

  const user = useSelector((state) => state.auth.user);
  const userpersonaldetails = useSelector(
    (state) => state.onboarding.userpersonaldetails
  );
  // Check if the logged-in user is a Super Admin (used for editing candidate profiles)
  const isSuperAdmin = (user?.role?.name as Roles) === Roles.SUPER_ADMIN;
  const stepCompletion: TCompletionStatus | null = useSelector(
    (state) => state.onboarding.completedstatus
  );
  const isIdVerificationStepCompleted = useSelector(
    (state) => state.onboarding.completedstatus?.id_verification
  );

  const connectIdData = userpersonaldetails?.connect_id_data
    ? userpersonaldetails?.connect_id_data
    : null;

  const [userDetails, setUserDetails] = useState<UserDetailsType | null>(null);
  const [checkPath, setCheckPath] = useState<boolean>(false);

  const redirectPersonalDetails = async () => {
    const result = await dispatch(setCompleteLaterAction(!false));
    if (result.payload) {
      dispatch(setShowNotVerifiedErrorAction(!true));
      handleNext();
    }
  };

  const handleVerificationSuccess = (userData: UserDetailsType) => {
    // console.log("Verification successful:", userData);
    setUserDetails(userData);
    // Proceed to next step after successful verification
    redirectPersonalDetails();
  };

  useEffect(() => {
    const urlSearchString = window.location.search;
    const params = new URLSearchParams(urlSearchString);
    const tokenString = params.get("tokens");

    if (tokenString !== null && tokenString !== "") {
      const checkToken = async () => {
        try {
          const sendValidTokenData = JSON.parse(tokenString);
          await dispatch(sendConnectIdDetailsThunk(sendValidTokenData));
        } catch (e) {
          console.error("Invalid JSON in tokens param", e);
        }
      };

      checkToken();
    }
  }, [dispatch]);

  useEffect(() => {
    if (location.pathname.includes("candidate")) {
      setCheckPath(true);
    }
  }, [location]);

  const handleNext = () => {
    if(isSuperAdmin) {
      navigate("/admin/candidate-profile/personal-details")
    }else if (checkPath) {
      navigate("/candidate/my-profile/personal-details");
    } else {
      navigate(AUTH_ROUTE.PERSONAL_DETAILS);
    }
  };

  return (
    
      <div className="d-flex flex-column gap-20 connect-id-new">
        <div className="fs-30 fw-400 text-black">ID Verification</div>
        { isImpersonating && !connectIdData && 
          <div className={`bg-teal-light px-15px py-10px radius-8px d-flex gap-6`}>
            <Warning />
            <div className="fs-13 fw-400 text-black lh-sm">
              You are currently impersonating a candidate. You cannot start the ID Verification while in impersonation mode.
            </div>
          </div>
        }
        {isIdVerificationStepCompleted === 1 && connectIdData ? (
          <div className="d-flex flex-column gap-20">
            <div className="text-black fs-16">
              Thank you, your details have been successfully verified.
            </div>
            <svg
              width="145"
              height="24"
              viewBox="0 0 145 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <rect width="145" height="24" rx="12" fill="#E9E9E9" />
              <path
                d="M16.8138 12.9658C16.8138 14.4791 15.5538 15.7034 13.9964 15.7034C12.439 15.7034 11.179 14.4791 11.179 12.9658C11.179 11.4525 12.439 10.2281 13.9964 10.2281C14.779 10.2281 15.4834 10.5323 15.992 11.0342L19.1772 7.93916C18.6686 7.44487 18.3555 6.76046 18.3555 6H15.992C15.992 7.01901 16.3051 7.97719 16.8451 8.76806C16.0312 8.24335 15.0451 7.93916 13.9964 7.93916C11.1321 7.93156 8.81555 10.1825 8.81555 12.9658C8.81555 15.7491 11.1321 18 13.9964 18C16.8607 18 19.1772 15.7491 19.1772 12.9658H16.8138Z"
                fill="#262626"
              />
              <path
                d="M26.81 15L24.21 8H25.29L27.41 13.96L29.55 8H30.61L28.01 15H26.81ZM33.1316 15.12C32.6516 15.12 32.2283 15.0067 31.8616 14.78C31.495 14.5533 31.205 14.2367 30.9916 13.83C30.785 13.4233 30.6816 12.95 30.6816 12.41C30.6816 11.8633 30.785 11.3833 30.9916 10.97C31.1983 10.5567 31.485 10.2367 31.8516 10.01C32.225 9.77667 32.655 9.66 33.1416 9.66C33.6483 9.66 34.075 9.77333 34.4216 10C34.775 10.2267 35.0416 10.53 35.2216 10.91C35.4083 11.29 35.5016 11.71 35.5016 12.17C35.5016 12.2367 35.5016 12.3067 35.5016 12.38C35.5016 12.4533 35.495 12.5367 35.4816 12.63H31.4216V11.92H34.5116C34.4983 11.46 34.3616 11.1067 34.1016 10.86C33.8416 10.6133 33.5183 10.49 33.1316 10.49C32.865 10.49 32.6216 10.5567 32.4016 10.69C32.1816 10.8167 32.0016 11.0067 31.8616 11.26C31.7283 11.5067 31.6616 11.82 31.6616 12.2V12.48C31.6616 12.8733 31.7283 13.2067 31.8616 13.48C31.995 13.7467 32.1716 13.95 32.3916 14.09C32.6116 14.2233 32.855 14.29 33.1216 14.29C33.4616 14.29 33.7283 14.2267 33.9216 14.1C34.115 13.9667 34.2616 13.7833 34.3616 13.55H35.3616C35.275 13.8433 35.1283 14.11 34.9216 14.35C34.7216 14.59 34.4683 14.78 34.1616 14.92C33.8616 15.0533 33.5183 15.12 33.1316 15.12ZM36.6531 15V9.78H37.5531L37.6331 10.73C37.7531 10.4967 37.9098 10.3033 38.1031 10.15C38.2965 9.99 38.5265 9.87 38.7931 9.79C39.0598 9.70333 39.3598 9.66 39.6931 9.66V10.72H39.1531C38.9531 10.72 38.7631 10.7467 38.5831 10.8C38.4031 10.8467 38.2431 10.9267 38.1031 11.04C37.9631 11.1533 37.8531 11.31 37.7731 11.51C37.6931 11.7033 37.6531 11.95 37.6531 12.25V15H36.6531ZM40.7173 15V9.78H41.7173V15H40.7173ZM41.2173 8.89C41.0239 8.89 40.8639 8.83 40.7373 8.71C40.6173 8.59 40.5573 8.43667 40.5573 8.25C40.5573 8.07 40.6173 7.92333 40.7373 7.81C40.8639 7.69 41.0239 7.63 41.2173 7.63C41.4039 7.63 41.5606 7.69 41.6873 7.81C41.8139 7.92333 41.8773 8.07 41.8773 8.25C41.8773 8.43667 41.8139 8.59 41.6873 8.71C41.5606 8.83 41.4039 8.89 41.2173 8.89ZM43.4568 15V10.63H42.6868V9.78H43.4568V9.28C43.4568 8.93333 43.5168 8.65 43.6368 8.43C43.7568 8.21 43.9301 8.05 44.1568 7.95C44.3835 7.85 44.6568 7.8 44.9768 7.8H45.4868V8.65H45.1268C44.8935 8.65 44.7235 8.7 44.6168 8.8C44.5101 8.89333 44.4568 9.06 44.4568 9.3V9.78H47.4068V15H46.4068V10.63H44.4568V15H43.4568ZM46.9168 8.88C46.7301 8.88 46.5735 8.82 46.4468 8.7C46.3201 8.58 46.2568 8.42667 46.2568 8.24C46.2568 8.06 46.3201 7.91333 46.4468 7.8C46.5735 7.68 46.7301 7.62 46.9168 7.62C47.1101 7.62 47.2668 7.68 47.3868 7.8C47.5135 7.91333 47.5768 8.06 47.5768 8.24C47.5768 8.42667 47.5135 8.58 47.3868 8.7C47.2668 8.82 47.1101 8.88 46.9168 8.88ZM51.1297 15.12C50.6497 15.12 50.2264 15.0067 49.8597 14.78C49.493 14.5533 49.203 14.2367 48.9897 13.83C48.783 13.4233 48.6797 12.95 48.6797 12.41C48.6797 11.8633 48.783 11.3833 48.9897 10.97C49.1964 10.5567 49.483 10.2367 49.8497 10.01C50.223 9.77667 50.653 9.66 51.1397 9.66C51.6464 9.66 52.073 9.77333 52.4197 10C52.773 10.2267 53.0397 10.53 53.2197 10.91C53.4064 11.29 53.4997 11.71 53.4997 12.17C53.4997 12.2367 53.4997 12.3067 53.4997 12.38C53.4997 12.4533 53.493 12.5367 53.4797 12.63H49.4197V11.92H52.5097C52.4964 11.46 52.3597 11.1067 52.0997 10.86C51.8397 10.6133 51.5164 10.49 51.1297 10.49C50.863 10.49 50.6197 10.5567 50.3997 10.69C50.1797 10.8167 49.9997 11.0067 49.8597 11.26C49.7264 11.5067 49.6597 11.82 49.6597 12.2V12.48C49.6597 12.8733 49.7264 13.2067 49.8597 13.48C49.993 13.7467 50.1697 13.95 50.3897 14.09C50.6097 14.2233 50.853 14.29 51.1197 14.29C51.4597 14.29 51.7264 14.2267 51.9197 14.1C52.113 13.9667 52.2597 13.7833 52.3597 13.55H53.3597C53.273 13.8433 53.1264 14.11 52.9197 14.35C52.7197 14.59 52.4664 14.78 52.1597 14.92C51.8597 15.0533 51.5164 15.12 51.1297 15.12ZM56.8212 15.12C56.3545 15.12 55.9412 15.0033 55.5812 14.77C55.2278 14.53 54.9512 14.2067 54.7512 13.8C54.5512 13.3867 54.4512 12.92 54.4512 12.4C54.4512 11.8667 54.5512 11.3967 54.7512 10.99C54.9578 10.5767 55.2412 10.2533 55.6012 10.02C55.9678 9.78 56.3878 9.66 56.8612 9.66C57.2545 9.66 57.5945 9.74 57.8812 9.9C58.1678 10.0533 58.3945 10.27 58.5612 10.55V7.8H59.5612V15H58.6712L58.5712 14.21C58.4712 14.37 58.3412 14.52 58.1812 14.66C58.0212 14.7933 57.8278 14.9033 57.6012 14.99C57.3812 15.0767 57.1212 15.12 56.8212 15.12ZM57.0112 14.25C57.3245 14.25 57.5978 14.1767 57.8312 14.03C58.0645 13.8767 58.2445 13.66 58.3712 13.38C58.4978 13.1 58.5612 12.77 58.5612 12.39C58.5612 12.01 58.4978 11.6833 58.3712 11.41C58.2445 11.13 58.0645 10.9133 57.8312 10.76C57.5978 10.6067 57.3245 10.53 57.0112 10.53C56.7178 10.53 56.4545 10.6067 56.2212 10.76C55.9878 10.9133 55.8045 11.13 55.6712 11.41C55.5378 11.6833 55.4712 12.01 55.4712 12.39C55.4712 12.77 55.5378 13.1 55.6712 13.38C55.8045 13.66 55.9878 13.8767 56.2212 14.03C56.4545 14.1767 56.7178 14.25 57.0112 14.25ZM64.6177 15L63.0777 9.78H64.0677L65.1577 13.88H65.1177L66.2877 9.78H67.4177L68.5877 13.87H68.5577L69.6377 9.78H70.6377L69.0977 15H68.0677L66.8377 10.74H66.8777L65.6477 15H64.6177ZM71.6352 15V9.78H72.6352V15H71.6352ZM72.1352 8.89C71.9419 8.89 71.7819 8.83 71.6552 8.71C71.5352 8.59 71.4752 8.43667 71.4752 8.25C71.4752 8.07 71.5352 7.92333 71.6552 7.81C71.7819 7.69 71.9419 7.63 72.1352 7.63C72.3219 7.63 72.4786 7.69 72.6052 7.81C72.7319 7.92333 72.7952 8.07 72.7952 8.25C72.7952 8.43667 72.7319 8.59 72.6052 8.71C72.4786 8.83 72.3219 8.89 72.1352 8.89ZM76.2048 15C75.8914 15 75.6181 14.95 75.3848 14.85C75.1514 14.75 74.9714 14.5833 74.8448 14.35C74.7181 14.1167 74.6548 13.8 74.6548 13.4V10.63H73.7448V9.78H74.6548L74.7748 8.44H75.6548V9.78H77.0948V10.63H75.6548V13.41C75.6548 13.6967 75.7114 13.8933 75.8248 14C75.9448 14.1 76.1548 14.15 76.4548 14.15H77.0848V15H76.2048ZM78.2547 15V7.8H79.2547V10.6C79.428 10.3133 79.6614 10.0867 79.9547 9.92C80.248 9.74667 80.568 9.66 80.9147 9.66C81.328 9.66 81.678 9.74 81.9647 9.9C82.2514 10.0533 82.468 10.2867 82.6147 10.6C82.7614 10.9133 82.8347 11.31 82.8347 11.79V15H81.8447V11.89C81.8447 11.4367 81.738 11.0967 81.5247 10.87C81.318 10.6367 81.028 10.52 80.6547 10.52C80.3947 10.52 80.158 10.58 79.9447 10.7C79.738 10.82 79.5714 10.9967 79.4447 11.23C79.318 11.4633 79.2547 11.7467 79.2547 12.08V15H78.2547ZM89.8398 15.12C89.1732 15.12 88.5932 14.97 88.0998 14.67C87.6132 14.3633 87.2365 13.94 86.9698 13.4C86.7032 12.8533 86.5698 12.22 86.5698 11.5C86.5698 10.7867 86.7032 10.1567 86.9698 9.61C87.2365 9.06333 87.6132 8.64 88.0998 8.34C88.5932 8.03333 89.1732 7.88 89.8398 7.88C90.6532 7.88 91.3132 8.07667 91.8198 8.47C92.3265 8.86333 92.6465 9.41667 92.7798 10.13H91.6998C91.5932 9.71667 91.3865 9.38667 91.0798 9.14C90.7732 8.89333 90.3598 8.77 89.8398 8.77C89.3798 8.77 88.9798 8.88 88.6398 9.1C88.3065 9.32 88.0498 9.63333 87.8698 10.04C87.6898 10.4467 87.5998 10.9333 87.5998 11.5C87.5998 12.0667 87.6898 12.5567 87.8698 12.97C88.0498 13.3767 88.3065 13.69 88.6398 13.91C88.9798 14.1233 89.3798 14.23 89.8398 14.23C90.3598 14.23 90.7732 14.1133 91.0798 13.88C91.3865 13.64 91.5932 13.3167 91.6998 12.91H92.7798C92.6465 13.6033 92.3265 14.1467 91.8198 14.54C91.3132 14.9267 90.6532 15.12 89.8398 15.12ZM96.2676 15.12C95.7809 15.12 95.3476 15.0067 94.9676 14.78C94.5942 14.5533 94.3009 14.2367 94.0876 13.83C93.8742 13.4167 93.7676 12.9367 93.7676 12.39C93.7676 11.8433 93.8742 11.3667 94.0876 10.96C94.3009 10.5467 94.5976 10.2267 94.9776 10C95.3576 9.77333 95.7909 9.66 96.2776 9.66C96.7709 9.66 97.2042 9.77333 97.5776 10C97.9576 10.2267 98.2509 10.5467 98.4576 10.96C98.6709 11.3667 98.7776 11.8433 98.7776 12.39C98.7776 12.9367 98.6709 13.4167 98.4576 13.83C98.2442 14.2367 97.9476 14.5533 97.5676 14.78C97.1942 15.0067 96.7609 15.12 96.2676 15.12ZM96.2676 14.26C96.5476 14.26 96.7976 14.19 97.0176 14.05C97.2442 13.91 97.4242 13.7033 97.5576 13.43C97.6909 13.15 97.7576 12.8033 97.7576 12.39C97.7576 11.9767 97.6909 11.6333 97.5576 11.36C97.4309 11.08 97.2576 10.87 97.0376 10.73C96.8176 10.59 96.5642 10.52 96.2776 10.52C96.0042 10.52 95.7542 10.59 95.5276 10.73C95.3009 10.87 95.1209 11.08 94.9876 11.36C94.8609 11.6333 94.7976 11.9767 94.7976 12.39C94.7976 12.8033 94.8609 13.15 94.9876 13.43C95.1209 13.7033 95.2976 13.91 95.5176 14.05C95.7376 14.19 95.9876 14.26 96.2676 14.26ZM99.9832 15V9.78H100.873L100.933 10.61C101.087 10.3167 101.307 10.0867 101.593 9.92C101.88 9.74667 102.217 9.66 102.603 9.66C102.997 9.66 103.337 9.73667 103.623 9.89C103.91 10.0433 104.13 10.2767 104.283 10.59C104.443 10.8967 104.523 11.28 104.523 11.74V15H103.533V11.84C103.533 11.4067 103.427 11.08 103.213 10.86C103 10.6333 102.707 10.52 102.333 10.52C102.087 10.52 101.86 10.5767 101.653 10.69C101.447 10.8033 101.283 10.9733 101.163 11.2C101.043 11.42 100.983 11.6933 100.983 12.02V15H99.9832ZM105.901 15V9.78H106.791L106.851 10.61C107.005 10.3167 107.225 10.0867 107.511 9.92C107.798 9.74667 108.135 9.66 108.521 9.66C108.915 9.66 109.255 9.73667 109.541 9.89C109.828 10.0433 110.048 10.2767 110.201 10.59C110.361 10.8967 110.441 11.28 110.441 11.74V15H109.451V11.84C109.451 11.4067 109.345 11.08 109.131 10.86C108.918 10.6333 108.625 10.52 108.251 10.52C108.005 10.52 107.778 10.5767 107.571 10.69C107.365 10.8033 107.201 10.9733 107.081 11.2C106.961 11.42 106.901 11.6933 106.901 12.02V15H105.901ZM114.069 15.12C113.589 15.12 113.166 15.0067 112.799 14.78C112.432 14.5533 112.142 14.2367 111.929 13.83C111.722 13.4233 111.619 12.95 111.619 12.41C111.619 11.8633 111.722 11.3833 111.929 10.97C112.136 10.5567 112.422 10.2367 112.789 10.01C113.162 9.77667 113.592 9.66 114.079 9.66C114.586 9.66 115.012 9.77333 115.359 10C115.712 10.2267 115.979 10.53 116.159 10.91C116.346 11.29 116.439 11.71 116.439 12.17C116.439 12.2367 116.439 12.3067 116.439 12.38C116.439 12.4533 116.432 12.5367 116.419 12.63H112.359V11.92H115.449C115.436 11.46 115.299 11.1067 115.039 10.86C114.779 10.6133 114.456 10.49 114.069 10.49C113.802 10.49 113.559 10.5567 113.339 10.69C113.119 10.8167 112.939 11.0067 112.799 11.26C112.666 11.5067 112.599 11.82 112.599 12.2V12.48C112.599 12.8733 112.666 13.2067 112.799 13.48C112.932 13.7467 113.109 13.95 113.329 14.09C113.549 14.2233 113.792 14.29 114.059 14.29C114.399 14.29 114.666 14.2267 114.859 14.1C115.052 13.9667 115.199 13.7833 115.299 13.55H116.299C116.212 13.8433 116.066 14.11 115.859 14.35C115.659 14.59 115.406 14.78 115.099 14.92C114.799 15.0533 114.456 15.12 114.069 15.12ZM119.911 15.12C119.424 15.12 118.991 15.0067 118.611 14.78C118.231 14.5467 117.931 14.2267 117.711 13.82C117.497 13.4133 117.391 12.9433 117.391 12.41C117.391 11.8567 117.497 11.3767 117.711 10.97C117.931 10.5633 118.231 10.2433 118.611 10.01C118.991 9.77667 119.424 9.66 119.911 9.66C120.524 9.66 121.037 9.82333 121.451 10.15C121.871 10.4767 122.137 10.92 122.251 11.48H121.211C121.144 11.18 120.991 10.9467 120.751 10.78C120.511 10.6067 120.227 10.52 119.901 10.52C119.614 10.52 119.357 10.5933 119.131 10.74C118.911 10.88 118.737 11.09 118.611 11.37C118.484 11.6433 118.421 11.9833 118.421 12.39C118.421 12.6967 118.454 12.9667 118.521 13.2C118.594 13.4333 118.697 13.63 118.831 13.79C118.964 13.95 119.121 14.07 119.301 14.15C119.481 14.23 119.681 14.27 119.901 14.27C120.121 14.27 120.321 14.2333 120.501 14.16C120.681 14.08 120.834 13.9667 120.961 13.82C121.087 13.6733 121.171 13.5 121.211 13.3H122.251C122.137 13.8467 121.871 14.2867 121.451 14.62C121.031 14.9533 120.517 15.12 119.911 15.12ZM125.472 15C125.159 15 124.886 14.95 124.652 14.85C124.419 14.75 124.239 14.5833 124.112 14.35C123.986 14.1167 123.922 13.8 123.922 13.4V10.63H123.012V9.78H123.922L124.042 8.44H124.922V9.78H126.362V10.63H124.922V13.41C124.922 13.6967 124.979 13.8933 125.092 14C125.212 14.1 125.422 14.15 125.722 14.15H126.352V15H125.472ZM127.572 15V8H128.572V15H127.572ZM130.082 15V8H132.032C132.845 8 133.515 8.14333 134.042 8.43C134.569 8.71667 134.959 9.12333 135.212 9.65C135.472 10.17 135.602 10.79 135.602 11.51C135.602 12.23 135.472 12.85 135.212 13.37C134.959 13.89 134.569 14.2933 134.042 14.58C133.515 14.86 132.845 15 132.032 15H130.082ZM131.082 14.16H131.992C132.632 14.16 133.142 14.0533 133.522 13.84C133.902 13.6267 134.172 13.3233 134.332 12.93C134.492 12.53 134.572 12.0567 134.572 11.51C134.572 10.9567 134.492 10.48 134.332 10.08C134.172 9.68 133.902 9.37333 133.522 9.16C133.142 8.94667 132.632 8.84 131.992 8.84H131.082V14.16Z"
                fill="#262626"
              />
            </svg>
            {userpersonaldetails?.is_connectid_verified !== null && connectIdData && (
              <>
                {/* <div className="fs-20 fw-500">Basic Details</div> */}

                <div className="d-flex gap-20 flex-wrap">
                  {connectIdData?.claims?.given_name && (
                    <div className="d-flex flex-column gap-2">
                      <div className="fs-13 text-grey-medium">Given Name</div>
                      <div className="fs-16">
                        {connectIdData?.claims?.given_name ?? "No data"}
                      </div>
                    </div>
                  )}
                  {connectIdData?.claims?.middle_name && (
                    <div className="d-flex flex-column gap-2">
                      <div className="fs-13 text-grey-medium">Middle Name</div>
                      <div className="fs-16">
                        {connectIdData?.claims?.middle_name ?? "No data"}
                      </div>
                    </div>
                  )}
                  {connectIdData?.claims?.family_name && (
                    <div className="d-flex flex-column gap-2">
                      <div className="fs-13 text-grey-medium">Family Name</div>
                      <div className="fs-16">
                        {connectIdData?.claims?.family_name ?? "No data"}
                      </div>
                    </div>
                  )}
                  {/* <div className="d-flex flex-column gap-2">
                    <div className="fs-13 text-grey-medium">Birth Date</div>
                    <div className="fs-16">
                      {connectIdData?.claims?.birthdate ?? "No data"}
                    </div>
                  </div> */}
                </div>
                {/* {connectIdData?.claims?.phone_number && (
                  <div className="d-flex flex-column gap-2">
                    <div className="fs-13 text-grey-medium">Phone Number</div>
                    <div className="fs-16">
                      {connectIdData?.claims?.phone_number ?? "No data"}
                    </div>
                  </div>
                )} */}
                {/* <div className="fs-20 fw-500">Address</div> */}

                {/* <div className="d-flex gap-40 flex-wrap">
                  {connectIdData?.claims?.address?.street_address && (
                    <div className="d-flex flex-column gap-2">
                      <div className="fs-13 text-grey-medium">
                        Street Address
                      </div>
                      <div className="fs-16">
                        {connectIdData?.claims?.address?.street_address ??
                          "No data"}
                      </div>
                    </div>
                  )}
                  {connectIdData?.claims?.address?.region && (
                    <div className="d-flex flex-column gap-2">
                      <div className="fs-13 text-grey-medium">Region</div>
                      <div className="fs-16">
                        {connectIdData?.claims?.address?.region ?? "No data"}
                      </div>
                    </div>
                  )}
                  {connectIdData?.claims?.address?.locality && (
                    <div className="d-flex flex-column gap-2">
                      <div className="fs-13 text-grey-medium">Locality</div>
                      <div className="fs-16">
                        {connectIdData?.claims?.address?.locality ?? "No data"}
                      </div>
                    </div>
                  )}
                  {connectIdData?.claims?.address?.postal_code && (
                    <div className="d-flex flex-column gap-2">
                      <div className="fs-13 text-grey-medium">Postal Code</div>
                      <div className="fs-16">
                        {connectIdData?.claims?.address?.postal_code ??
                          "No data"}
                      </div>
                    </div>
                  )}
                  {connectIdData?.claims?.address?.country && (
                    <div className="d-flex flex-column gap-2">
                      <div className="fs-13 text-grey-medium">Country</div>
                      <div className="fs-16">
                        {connectIdData?.claims?.address?.country ?? "No data"}
                      </div>
                    </div>
                  )}
                </div> */}
              </>
            )}
            {!isSuperAdmin && (
              <div className="d-flex">
                <CustomButton
                  type="submit"
                  variant="primary"
                  onClick={handleNext}
                  className="btn-theme bg-teal text-black mobile-w-100"
                >
                  Next
                  <RightArrow color={"#000000"} />
                </CustomButton>
              </div>
            )}
          </div>
        ) : (
          <>
            {/* <div className="fs-16 fw-400 text-black">
              Verify your identity using only the data that is necessary with
              ConnectID. ConnectID does not see or store your personal data.
            </div> */}
            {/* <div className="d-flex">
              <div className="border border-black radius-8px p-20px d-flex flex-column gap-10 w-100 max-w-600px">
                <div className="fs-16 fw-400 text-black">How it works</div>
                <div className="position-relative py-10px w-358px mobile-w-198px">
                  <div className="h-1px bg-black"></div>
                  <div
                    className="position-absolute"
                    style={{ top: "-1px", left: "-4px" }}
                  >
                    <RadioFilled />
                  </div>
                  <div
                    className="position-absolute mobile-left-88px"
                    style={{ top: "-1px", left: "168px" }}
                  >
                    <RadioFilled />
                  </div>
                  <div
                    className="position-absolute"
                    style={{ top: "-1px", right: "-4px" }}
                  >
                    <RadioFilled />
                  </div>
                </div>
                <div className="d-flex gap-10 fs-13 fw-400 text-black">
                  <div className="w-160px mobile-w-80px">
                    Select your trusted organisation
                  </div>
                  <div className="w-160px mobile-w-80px">
                    Log in to your existing account
                  </div>
                  <div className="w-160px mobile-w-100px">
                    Review your details and provide consent
                  </div>
                </div>
              </div>
            </div> */}
            {/* <div className="d-flex justify-content-center justify-content-lg-start">
              <HoverTooltip
                position="top"
                content={
                  <div className="fs-13 fw-400 text-black">
                    ConnectID is an Australian-owned digital identity solution which
                    allows you to securely prove who you are. ConnectID does not see
                    or store your personal data. Visit{" "}
                    <a
                      className="fw-700 text-black"
                      href="https://connectid.com.au/"
                      target="_blank"
                    >
                      connectid.com.au
                    </a>{" "}
                    to find out more.
                  </div>
                }
              >
                <div className="d-flex gap-6 align-items-center">
                  <Warning />
                  <div className="fs-13 fw-400 text-black">What's ConnectID?</div>
                </div>
              </HoverTooltip>
            </div> */}
            {/* <div className="h-1px"></div> */}
            <div className="d-flex flex-column gap-20">
              {
                !isImpersonating && !isSuperAdmin && (
                  <ConnectIDButton
                    onSuccess={handleVerificationSuccess}
                    claims={[
                      "given_name",
                      "middle_name",
                      "family_name",
                      // "email",
                      // "phone_number",
                      // "address",
                      // "birthdate",
                    ]}
                    buttonLabel="Verify"
                    theme="light"
                  />
                )
              }              
              <CustomButton
                type="button"
                variant="secondary"
                onClick={redirectPersonalDetails}
                className="btn-theme bg-off-white text-black"
              >
                Complete Later
              </CustomButton>
            </div>
          </>
        )}
      </div>
    
  );
};

export default IdVerfication;
